/**
 * ============================================================================
 * DOCUMENTS STORE - CORE STORE INITIALIZATION
 * ============================================================================
 * 
 * This file initializes the main documents store using the GenericStore pattern.
 * The store handles caching and syncing of document items for games.
 * 
 * Architecture:
 * - Uses storeRegistry for persistent caching
 * - 30-minute TTL for cached data
 * - Supports local-first approach with background sync
 * - Supports optimistic updates for instant UI feedback
 * 
 * @module documents/store
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import type { DocumentItem } from "./types";

// ============================================================================
// STORE CONFIGURATION
// ============================================================================

/** Cache time-to-live in milliseconds (30 minutes) */
const CACHE_TTL_MS = 1000 * 60 * 30;

/** Store key used for persistent caching */
const STORE_KEY = 'document_items';

// ============================================================================
// STORE INSTANCE
// ============================================================================

/**
 * Main store instance for document items.
 * 
 * Features:
 * - Persistent caching with configurable TTL
 * - Automatic sync from remote API
 * - Optimistic update support
 * - Local-first approach with background sync
 * 
 * @example
 * ```ts
 * // Get all cached data
 * const allDocuments = documentStore.getData();
 * 
 * // Update data
 * documentStore.setData(newData);
 * 
 * // Sync from remote
 * await documentStore.syncFromRemote(fetchFn);
 * ```
 */
export const documentStore = storeRegistry.getStore<DocumentItem>({
  key: STORE_KEY,
  ttlMs: CACHE_TTL_MS,
});

// ============================================================================
// STORE UTILITIES
// ============================================================================

/**
 * Clear the documents cache.
 * Call this when you need to force a fresh fetch from the API.
 */
export async function clearDocumentCache(): Promise<void> {
  documentStore.setData([]);
  console.log('[DocumentsStore] Cache cleared successfully');
}
